import {IAppliedRule, IRuleResult, RuleResultType} from '../incase-domain/appliedRules';
import {IPresentation} from '../incase-domain/presentation';
import {ElementInteraction, IRunFile} from '../incase-domain/proptypes/runfile';
import {interactiveElementTypes} from '../incase-domain/elements/base/ElementTypes';
import {IElement} from '../incase-domain/elements';
import {checkIfRuleIsSatisfied} from './rules/ruleChecker';

export const applyRulesToPresentation = (presentation: IPresentation, runFile: IRunFile): IPresentation => {
    const findElementInteraction = (runFile: IRunFile, element: IElement): ElementInteraction => {
        if (!runFile.elementInteractions) return {};
        return (runFile.elementInteractions[element.slideId] || {})[element.id] || {};
    };
    const interactiveElements = presentation.slides
        .filter((slide) => slide.elements && slide.elements.length > 0)
        .reduce((elements: IElement[], slide) => {
            return [...elements, ...slide.elements];
        }, [])
        .filter((element) => interactiveElementTypes.includes(element.type))
        .map((element) => {
            return {
                ...element,
                elementInteractions: findElementInteraction(runFile, element),
            };
        });

    const checkedRules = presentation.appliedRules?.map((rule) => checkIfRuleIsSatisfied(rule, interactiveElements)) || [];
    const updatedPresentation = {
        ...presentation,
        appliedRules: checkedRules,
    };

    return applySlidesRules(updatedPresentation);
};

const applySlidesRules = (presentation: IPresentation): IPresentation => {
    const filterList = getFilterList(presentation);
    const jumpToFilterList = getJumpToFilterList(presentation);
    const combinedFilter = [...new Set([...filterList, ...jumpToFilterList])];

    // get filter list - list of slides that don't show in presentation
    const filteredSlides = presentation.slides.filter((slide) => {
        return !combinedFilter.includes(slide._id);
    });
    return {...presentation, slides: filteredSlides};
};

const getJumpToFilterList = (presentation: IPresentation): string[] => {
    const result: string[] = [];
    if (!presentation.appliedRules?.length) return result;

    presentation.appliedRules?.forEach((rule) => {
        if (!rule.satisfied) return;

        rule.results?.forEach((ruleResult) => {
            if (ruleResult.resultType !== RuleResultType.JUMP_TO_SLIDE) return;

            const destSlideId = ruleResult.slideIds[0];
            if (!destSlideId) return;

            const elementId = rule.predicates[0]?.elementId;
            const sourceSlide = presentation.slides.find((s) => s.elements?.some((e) => e.id === elementId));
            if (!sourceSlide) return;

            const sourceIdx = presentation.slideIndexList.indexOf(sourceSlide._id);
            const destIdx = presentation.slideIndexList.indexOf(destSlideId);

            if (sourceIdx === -1 || destIdx === -1 || destIdx <= sourceIdx + 1) return;

            result.push(...presentation.slideIndexList.slice(sourceIdx + 1, destIdx));
        });
    });

    return result;
};

const getFilterList = (presentation: IPresentation): string[] => {
    const filterList = [] as string[];
    if (presentation.appliedRules?.length) {
        presentation.slides.forEach((slide) => {
            const addToList = addToFilterList(presentation.appliedRules, presentation.slideIndexList, slide._id);
            if (addToList) {
                filterList.push(slide._id);
            }
        });
    }

    return filterList;
};

const addToFilterList = (appliedRules: IAppliedRule[] | undefined, slideIndexList: string[], slideId: string): boolean =>
    !!appliedRules &&
    appliedRules.some((rule) => rule.results.some((result) => checkIfShow(result, slideId, !!rule.satisfied, slideIndexList)));

const checkIfShow = (result: IRuleResult, slideId: string, isSatisfied: boolean, slideIndexList: string[]): boolean => {
    const isAfterBlockedSlide = slideIndexList.indexOf(slideId) > slideIndexList.indexOf(result.slideIds[0]);
    switch (result.resultType) {
        case RuleResultType.SHOW_SLIDES:
            return !isSatisfied && result.slideIds.includes(slideId);
        case RuleResultType.HIDE_SLIDES:
            return isSatisfied && result.slideIds.includes(slideId);
        case RuleResultType.BLOCK_SLIDE:
            return isSatisfied && isAfterBlockedSlide;
        case RuleResultType.JUMP_TO_SLIDE:
            return false; // zasebna kalkulacija u getJumpToFilterList
        default:
            return false;
    }
};
